import React from 'react';
import { jsx } from '@emotion/core';
import { IInputProps } from '../../utils/types';
import Input from '../input/Input';
import useDropDown from '../../hooks/useDropDown';
import DatePicker, { ICalenderEventObject } from './DatePicker';
import { format, isValid, parse, isBefore, isAfter } from 'date-fns';
import InputLabel from '../input/components/InputLabel';
/** @jsx jsx */
const MIN_DATE = new Date(1900, 0, 1);

interface IState {
  date: Date;
  inputState?: string;
}

interface IProps extends IInputProps {
  defaultDate?: Date;
  minDate?: Date;
  maxDate?: Date;
}

const DateInput: React.FC<IProps> = ({
  label,
  name,
  defaultDate,
  minDate,
  maxDate,
  onChange = () => {},
}) => {
  const fallbackDate = React.useMemo(() => maxDate ?? new Date(), [maxDate]);

  const validDefaultDate = React.useMemo(() => {
    if (!defaultDate) return null;
    const date = new Date(defaultDate);
    return isValid(date) ? date : null;
  }, [defaultDate]);

  const [state, setState] = React.useState<IState>({
    date: validDefaultDate ?? fallbackDate,
    inputState: validDefaultDate ? format(validDefaultDate, 'dd/MM/yyyy') : '',
  });

  const { elRef, isOpen, setIsOpen, handleVisibilityChange } = useDropDown(false);

  React.useEffect(() => {
    if (validDefaultDate) {
      setState({
        date: validDefaultDate,
        inputState: format(validDefaultDate, 'dd/MM/yyyy'),
      });
    } else {
      setState((prev) => ({
        ...prev,
        inputState: '',
        date: fallbackDate,
      }));
    }
  }, [validDefaultDate, fallbackDate]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, inputState: e.target.value });
  };

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (!value.trim()) {
      setState({ ...state, inputState: '' });
      onChange({ target: { name, value: '' } });
      return;
    }

    const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

    if (!isValid(parsedDate)) {
      setState({ ...state, inputState: '' });
      onChange({ target: { name, value: '' } });
      return;
    }

    const effectiveMin = minDate ?? MIN_DATE;
    if (isBefore(parsedDate, effectiveMin) || (maxDate && isAfter(parsedDate, maxDate))) {
      setState({ ...state, inputState: '' });
      onChange({ target: { name, value: '' } });
      return;
    }

    setState({
      date: parsedDate,
      inputState: format(parsedDate, 'dd/MM/yyyy'),
    });
    onChange({ target: { name, value: parsedDate } });
  };

  const handleOnEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 13) {
      e.currentTarget.blur();
      setIsOpen(false);
    }
  };

  const selectDate = (e: ICalenderEventObject) => {
    const { value } = e;
    const dateValue = new Date(value);
    
    if (!isValid(dateValue)) return;

    setState({
      date: dateValue,
      inputState: format(dateValue, 'dd/MM/yyyy'),
    });
    setIsOpen(false);
    onChange({ target: { name, value: dateValue } });
  };

  const onInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!isOpen) handleVisibilityChange(e);
  };

  return (
    <div>
      <InputLabel>{label}</InputLabel>
      <div ref={elRef} css={{ position: 'relative', width: '100%' }}>
        <Input
          placeholder="DD/MM/YYYY"
          onBlur={onInputBlur}
          onKeyDown={handleOnEnterKeyPress}
          value={state.inputState}
          onChange={onInputChange}
          onFocus={onInputFocus}
        />
        {isOpen && (
          <DatePicker
            minDate={minDate}
            maxDate={maxDate}
            onChange={selectDate}
            selectedDate={state.date}
            defaultDate={state.date}
          />
        )}
      </div>
    </div>
  );
};

export default DateInput;